import React, { useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useEvents } from '../contexts/EventContext';
import { useSport } from '../contexts/SportContext';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const EventCreationModal = ({ coords, onClose }) => {
    const { createEvent } = useEvents();
    const { sportTypes } = useSport();
    const { user } = useAuth();
    const { t } = useTranslation();

    const fallbackSportName = t('event_creation_unknown_sport', { defaultValue: 'Vibe' });
    const favoriteSportName = sportTypes[user?.favorite_sport]?.name || fallbackSportName;
    const modalTitle = t('event_creation_title');
    const modalSubtitle = t('event_creation_subtitle', { sportName: favoriteSportName });

    const getRoundedTime = () => {
        // Einfacher Ansatz: Lokale Zeit verwenden (nicht UTC-Konversion)
        const now = new Date();
        now.setHours(now.getHours() + 3);
        now.setMinutes(0);
        now.setSeconds(0);
        now.setMilliseconds(0);
        
        // Für datetime-local: Format ist YYYY-MM-DDTHH:MM (lokale Zeit)
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const [formData, setFormData] = useState({
        title: t('default_event_title', { sportName: favoriteSportName }),
        event_date: getRoundedTime(),
        max_participants: '',
        description: '',
        latitude: coords.lat,
        longitude: coords.lng,
        recurrence: null, // null | 'daily' | 'weekly' | 'monthly'
    });
    const [isPublic, setIsPublic] = useState(true);
    const { showError, showSuccess } = useToast();
    const [loading, setLoading] = useState(false);

    const handleChange = e => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async e => {
        e.preventDefault();
        setLoading(true);

        // Frontend-Validierung vor Submit
        const maxParticipants = formData.max_participants ? parseInt(formData.max_participants) : null;
        if (maxParticipants && (maxParticipants < 2 || maxParticipants > 100)) {
            showError(t('event_creation_error_max_participants'));
            setLoading(false);
            return;
        }
        if (formData.latitude < -90 || formData.latitude > 90 || formData.longitude < -180 || formData.longitude > 180) {
            showError(t('event_creation_error_invalid_coords'));
            setLoading(false);
            return;
        }
        if (!formData.title || formData.title.trim().length === 0) {
            showError(t('event_creation_error_title_required'));
            setLoading(false);
            return;
        }
        // Konvertiere lokale event_date zu UTC (datetime-local -> UTC für Backend)
        const localDate = new Date(formData.event_date);
        const utcDate = new Date(localDate.getTime() - (localDate.getTimezoneOffset() * 60000));
        // To avoid timezone misinterpretation on the server we keep the full ISO string
        // including the trailing "Z" instead of trimming it off. The backend will
        // then parse it as UTC and the internal validation (date > now) works
        // consistently regardless of the server's local timezone.
        const event_date_utc = utcDate.toISOString();
        const eventData = {
            ...formData,
            event_date: event_date_utc, // send ISO 8601 with Z (UTC)
            max_participants: maxParticipants,
            is_public: isPublic,
            sport_type: user?.favorite_sport
        };
        try {
            const response = await createEvent(eventData);
            showSuccess(t('event_created_success', 'Event created!'));
            onClose();
        } catch (err) {
            const fallbackError = t('event_creation_error_generic');
            const errorMessage = err.response?.data?.message || err.message || fallbackError;
            showError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <motion.div
                className="modal-content floating-modal"
                onClick={e => e.stopPropagation()}
                initial={{ scale: 0.8, y: 100, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 120, damping: 20 }}
                style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f3f7ff 100%)',
                    borderRadius: '20px',
                    boxShadow: '0 10px 40px rgba(99, 102, 241, 0.12)',
                    maxWidth: 'min(500px, 95vw)',
                    width: '90vw',
                    maxHeight: 'min(90vh, 600px)',
                    overflow: 'auto',
                    padding: 'clamp(20px, 5vw, 30px) 25px'
                }}
            >
                <h2 style={{ textAlign: 'center', marginTop: 0, marginBottom: '10px', fontSize: 'clamp(1.3em, 4vw, 1.6em)', color: '#1a202c', fontWeight: '700' }}>{modalTitle}</h2>
                <p style={{ textAlign: 'center', color: '#64748b', fontSize: 'clamp(0.85em, 3vw, 0.95em)', marginBottom: '25px', fontWeight: '500' }}>{modalSubtitle}</p>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Grid Layout für kompakte Anordnung */}
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1.2fr', // Datum-Feld bekommt mehr Platz
                        gap: '12px'
                    }}>
                        {/* Gonkle-Name - Primäres Feld */}
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '4px',
                            border: '2px solid rgba(99, 102, 241, 0.2)',
                            borderRadius: 8,
                            padding: '4px',
                            backgroundColor: 'rgba(99, 102, 241, 0.02)'
                        }}>
                            <label style={{ fontWeight: 600, color: '#1a202c', fontSize: '0.9em', marginBottom: '2px' }}>{t('event_creation_label_title', 'Gonkle-Name')}</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                style={{ 
                                    padding: '12px', 
                                    borderRadius: 6, 
                                    border: '1px solid #d1d5db', 
                                    fontSize: '1em',
                                    minHeight: '44px',
                                    boxSizing: 'border-box',
                                    backgroundColor: '#fff'
                                }}
                                placeholder={t('default_event_title', { sportName: favoriteSportName })}
                            />
                        </div>
                        
                        {/* Datum/Uhrzeit - Primäres Feld */}
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '4px',
                            border: '2px solid rgba(99, 102, 241, 0.2)',
                            borderRadius: 8,
                            padding: '4px',
                            backgroundColor: 'rgba(99, 102, 241, 0.02)',
                            overflow: 'hidden' // Verhindert Overflow
                        }}>
                            <label style={{ fontWeight: 600, color: '#1a202c', fontSize: '0.9em', marginBottom: '2px' }}>{t('event_creation_label_when', 'Datum & Uhrzeit')}</label>
                            <input
                                type="datetime-local"
                                name="event_date"
                                value={formData.event_date}
                                onChange={handleChange}
                                required
                                style={{ 
                                    padding: '10px 6px', // Weniger padding für Container
                                    borderRadius: 4, // Kleinere radius für Container
                                    border: '1px solid #d1d5db', 
                                    fontSize: '0.85em', // Kleinere Schrift
                                    minHeight: '44px',
                                    boxSizing: 'border-box',
                                    backgroundColor: '#fff',
                                    width: '100%', // Sicherstellen dass es in Container passt
                                    maxWidth: '100%' // Verhindert Overflow
                                }}
                            />
                        </div>
                        
                        {/* Teilnehmerzahl - Sekundäres Feld */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontWeight: 600, color: '#1a202c', fontSize: '0.9em', marginBottom: '2px' }}>{t('event_creation_label_max_players', 'Max. Teilnehmer')}</label>
                            <input
                                type="number"
                                name="max_participants"
                                value={formData.max_participants}
                                onChange={handleChange}
                                min={2}
                                max={100}
                                placeholder={t('event_creation_max_players_placeholder', 'Leer = unbegrenzt')}
                                style={{ 
                                    padding: '12px', 
                                    borderRadius: 8, 
                                    border: '1px solid #d1d5db', 
                                    fontSize: '1em',
                                    minHeight: '44px',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        
                        {/* Sichtbarkeit - Sekundäres Feld */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontWeight: 600, color: '#1a202c', fontSize: '0.9em', marginBottom: '2px' }}>{t('event_creation_open_label', 'Öffentlich für alle')}</label>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                padding: '12px',
                                borderRadius: 8,
                                border: '1px solid #d1d5db',
                                minHeight: '44px',
                                boxSizing: 'border-box',
                                backgroundColor: '#fff'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={isPublic}
                                    onChange={() => setIsPublic(v => !v)}
                                    style={{ 
                                        accentColor: '#6366f1', 
                                        width: '20px', 
                                        height: '20px',
                                        cursor: 'pointer'
                                    }}
                                />
                                <span style={{ fontSize: '0.9em', color: '#1a202c' }}>
                                    {isPublic ? 'Ja' : 'Nein'}
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Beschreibung - volle Breite */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontWeight: 600, color: '#1a202c', fontSize: '0.9em', marginBottom: '2px' }}>{t('event_creation_label_description', 'Beschreibung')}</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={1}
                            style={{ 
                                padding: '12px', 
                                borderRadius: 8, 
                                border: '1px solid #d1d5db', 
                                fontSize: '1em',
                                minHeight: '44px',
                                boxSizing: 'border-box',
                                resize: 'vertical'
                            }}
                            placeholder={t('event_creation_description_placeholder', 'Optional')}
                        />
                    </div>
                    
                    {/* Wiederholung - 4 Checkboxes */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ fontWeight: 600, color: '#1a202c', fontSize: '0.9em', marginBottom: '2px' }}>Wiederholung:</label>
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr', 
                            gap: '8px',
                            padding: '12px',
                            borderRadius: 8,
                            backgroundColor: 'rgba(99, 102, 241, 0.05)',
                            border: '1px solid rgba(99, 102, 241, 0.1)'
                        }}>
                            <label style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                cursor: 'pointer',
                                padding: '8px',
                                borderRadius: 6,
                                backgroundColor: !formData.recurrence ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                                transition: 'all 0.2s'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={!formData.recurrence}
                                    onChange={() => setFormData(prev => ({ ...prev, recurrence: null }))}
                                    style={{ 
                                        accentColor: '#6366f1', 
                                        width: '18px', 
                                        height: '18px',
                                        cursor: 'pointer'
                                    }}
                                />
                                <span style={{ fontSize: '0.9em', color: '#1a202c', fontWeight: '500' }}>
                                    {t('event_creation_repeat_once')}
                                </span>
                            </label>
                            
                            <label style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                cursor: 'pointer',
                                padding: '8px',
                                borderRadius: 6,
                                backgroundColor: formData.recurrence === 'daily' ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                                transition: 'all 0.2s'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={formData.recurrence === 'daily'}
                                    onChange={() => setFormData(prev => ({ ...prev, recurrence: 'daily' }))}
                                    style={{ 
                                        accentColor: '#6366f1', 
                                        width: '18px', 
                                        height: '18px',
                                        cursor: 'pointer'
                                    }}
                                />
                                <span style={{ fontSize: '0.9em', color: '#1a202c', fontWeight: '500' }}>
                                    {t('event_creation_repeat_daily')}
                                </span>
                            </label>
                            
                            <label style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                cursor: 'pointer',
                                padding: '8px',
                                borderRadius: 6,
                                backgroundColor: formData.recurrence === 'weekly' ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                                transition: 'all 0.2s'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={formData.recurrence === 'weekly'}
                                    onChange={() => setFormData(prev => ({ ...prev, recurrence: 'weekly' }))}
                                    style={{ 
                                        accentColor: '#6366f1', 
                                        width: '18px', 
                                        height: '18px',
                                        cursor: 'pointer'
                                    }}
                                />
                                <span style={{ fontSize: '0.9em', color: '#1a202c', fontWeight: '500' }}>
                                    {t('event_creation_repeat_weekly')}
                                </span>
                            </label>
                            
                            <label style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                cursor: 'pointer',
                                padding: '8px',
                                borderRadius: 6,
                                backgroundColor: formData.recurrence === 'monthly' ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                                transition: 'all 0.2s'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={formData.recurrence === 'monthly'}
                                    onChange={() => setFormData(prev => ({ ...prev, recurrence: 'monthly' }))}
                                    style={{ 
                                        accentColor: '#6366f1', 
                                        width: '18px', 
                                        height: '18px',
                                        cursor: 'pointer'
                                    }}
                                />
                                <span style={{ fontSize: '0.9em', color: '#1a202c', fontWeight: '500' }}>
                                    {t('event_creation_repeat_monthly')}
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Fehler werden über Toast angezeigt, kein lokaler error-State */}
                    <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                        <button 
                            type="button" 
                            onClick={onClose}
                            style={{ 
                                flex: 1,
                                padding: '12px', 
                                borderRadius: 8, 
                                fontSize: '1em', 
                                fontWeight: '600',
                                border: '1px solid #d1d5db',
                                backgroundColor: '#fff',
                                color: '#6b7280',
                                cursor: 'pointer',
                                minHeight: '44px'
                            }}
                        >
                            {t('cancel_button', 'Abbrechen')}
                        </button>
                        <button 
                            type="submit" 
                            disabled={loading} 
                            className="button-primary" 
                            style={{ 
                                flex: 1,
                                padding: '12px', 
                                borderRadius: 8, 
                                fontSize: '1em', 
                                fontWeight: '600',
                                minHeight: '44px',
                                cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {loading ? t('event_creation_submit_button_loading') : t('event_creation_submit_button')}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

export default EventCreationModal;
